import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  TextField,
  IconButton,
} from "@mui/material";
import { ContentCopy, Delete } from "@mui/icons-material";
import Header from "../../components/Header";
import {
  useGetReviewQuery,
  useGetBibliographyListQuery,
  useGetPrismaCountsQuery,
  useGetStudyGroupsQuery,
  useCreateStudyGroupMutation,
  useDeleteStudyGroupMutation,
} from "../../services/review.service";
import { downloadReviewFile, fetchReviewBlob } from "../../services/reviewDownload";

function downloadSvgAsPng(svgText, filename) {
  const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
  const url = URL.createObjectURL(svgBlob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    canvas.toBlob((blob) => {
      const pngUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = pngUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(pngUrl);
    }, "image/png");
    URL.revokeObjectURL(url);
  };
  img.src = url;
}

const BibliographySection = ({ reviewId }) => {
  const { data, isLoading, error } = useGetBibliographyListQuery(reviewId);
  const [copied, setCopied] = useState(false);

  if (error) return <Alert severity="warning">{error.data?.message || "Could not load the bibliography."}</Alert>;
  if (isLoading) return <Typography>Loading...</Typography>;

  const citations = data?.citations || [];

  const copyAll = async () => {
    await navigator.clipboard.writeText(citations.join("\n\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" mb={1}>Final bibliography ({citations.length} included)</Typography>
      {citations.length === 0 ? (
        <Alert severity="info">No records are finally included yet.</Alert>
      ) : (
        <>
          <Box sx={{ maxHeight: 300, overflowY: "auto", mb: 2 }}>
            {citations.map((c, i) => (
              <Typography key={i} variant="body2" sx={{ mb: 1 }}>{c}</Typography>
            ))}
          </Box>
          <Box display="flex" flexWrap="wrap" gap={1}>
            <Button startIcon={<ContentCopy />} variant="outlined" onClick={copyAll}>
              {copied ? "Copied!" : "Copy Vancouver list"}
            </Button>
            <Button variant="outlined" onClick={() => downloadReviewFile(`${reviewId}/exports/bibliography?format=ris`, "bibliography.ris")}>
              Download RIS
            </Button>
            <Button variant="outlined" onClick={() => downloadReviewFile(`${reviewId}/exports/bibliography?format=bibtex`, "bibliography.bib")}>
              Download BibTeX
            </Button>
            <Button variant="outlined" onClick={() => downloadReviewFile(`${reviewId}/exports/bibliography?format=csv`, "bibliography.csv")}>
              Download CSV
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

const PrismaSection = ({ reviewId }) => {
  const { data: countsData } = useGetPrismaCountsQuery(reviewId);
  const [svgText, setSvgText] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    // Re-fetches whenever `countsData` changes, not just on mount: counts
    // come from RTK Query and share the ReviewStudyGroups cache tag with
    // getStudyGroups, so grouping/ungrouping reports into studies refetches
    // the counts -- and, via this dependency, the diagram image too.
    let cancelled = false;
    fetchReviewBlob(`${reviewId}/exports/prisma.svg`)
      .then((blob) => blob.text())
      .then((text) => { if (!cancelled) setSvgText(text); })
      .catch((err) => setError(err?.response?.data?.message || "Could not load the PRISMA diagram."));
    return () => { cancelled = true; };
  }, [reviewId, countsData]);

  const pending = countsData?.counts?.pending;
  const pendingTotal = pending ? pending.stage1 + pending.stage2 + pending.stage3 : 0;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" mb={1}>PRISMA 2020 flow diagram</Typography>
      {pendingTotal > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Still pending: Stage 1 ({pending.stage1}), Stage 2 ({pending.stage2}), Stage 3 ({pending.stage3}).
          The diagram reflects current counts and will change as screening continues.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {svgText && (
        <>
          <Box
            sx={{ overflowX: "auto", backgroundColor: "#fff", p: 1, borderRadius: 1 }}
            dangerouslySetInnerHTML={{ __html: svgText }}
          />
          <Box display="flex" gap={1} mt={2}>
            <Button
              variant="outlined"
              onClick={() => downloadReviewFile(`${reviewId}/exports/prisma.svg`, "prisma-flow-diagram.svg")}
            >
              Download SVG
            </Button>
            <Button variant="outlined" onClick={() => downloadSvgAsPng(svgText, "prisma-flow-diagram.png")}>
              Download PNG
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

const StudyGroupsPanel = ({ reviewId }) => {
  const { data, isLoading } = useGetStudyGroupsQuery(reviewId);
  const [createGroup] = useCreateStudyGroupMutation();
  const [deleteGroup] = useDeleteStudyGroupMutation();
  const [selected, setSelected] = useState([]);
  const [label, setLabel] = useState("");

  if (isLoading || !data) return <Typography>Loading...</Typography>;

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async () => {
    if (selected.length < 2) return;
    await createGroup({ reviewId, label: label || undefined, recordIds: selected });
    setSelected([]);
    setLabel("");
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" mb={1}>Group included reports into studies</Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        By default, one report = one study. Select two or more reports that are actually the same study to group them.
      </Typography>

      {data.groups.length > 0 && (
        <List dense>
          {data.groups.map((g) => (
            <ListItem
              key={g._id}
              secondaryAction={
                <IconButton size="small" onClick={() => deleteGroup({ reviewId, groupId: g._id })}>
                  <Delete fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={g.label || "(untitled study)"}
                secondary={g.records.map((r) => r.title).join("; ")}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Divider sx={{ my: 1 }} />
      <Typography variant="body2" mb={1}>Ungrouped included reports</Typography>
      <List dense>
        {data.ungroupedIncludedRecords.map((r) => (
          <ListItem key={r._id} disableGutters>
            <Checkbox checked={selected.includes(r._id)} onChange={() => toggle(r._id)} size="small" />
            <ListItemText primary={r.title} />
          </ListItem>
        ))}
      </List>
      {selected.length >= 2 && (
        <Box display="flex" gap={1} mt={1}>
          <TextField size="small" label="Study label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
          <Button variant="contained" onClick={submit}>Group {selected.length} reports</Button>
        </Box>
      )}
    </Paper>
  );
};

const Exports = () => {
  const { reviewId } = useParams();
  const { data } = useGetReviewQuery(reviewId);
  const isOwner = data?.isGlobalAdmin || data?.membership?.role === "owner";

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="Exports" subtitle="Bibliography, exclusion and decision logs, and the PRISMA 2020 diagram" />
      <Divider sx={{ my: 2 }} />

      <BibliographySection reviewId={reviewId} />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" mb={1}>Other downloads</Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          <Button variant="outlined" onClick={() => downloadReviewFile(`${reviewId}/exports/exclusions.csv`, "full-text-exclusions.csv")}>
            Full-text exclusions (CSV)
          </Button>
          <Button variant="outlined" onClick={() => downloadReviewFile(`${reviewId}/exports/decision-log.csv`, "decision-log.csv")}>
            Full decision log (CSV)
          </Button>
        </Box>
      </Paper>

      <PrismaSection reviewId={reviewId} />

      {isOwner && <StudyGroupsPanel reviewId={reviewId} />}
    </Box>
  );
};

export default Exports;
